// ShareableIdCard — Phase R5 "share your UAID" affordance.
//
// Renders the agent's Unique Agent ID with a one-tap copy button.
// Visually distinct from the rest of the cards (cyan→indigo gradient,
// icon flourish, "SHAREABLE AGENT ID" eyebrow) so the agent immediately
// understands this is the handle they give to OTHER agents — not
// their internal user record.
//
// Phase 4 follow-up (2026-05-11): moved out of AccountPanel and into
// the Players hub's "Players" sub-tab (the user list), sitting above
// the Add Player toolbar. The card is the most-used affordance for
// inbound player transfers, so it belongs next to the people surface
// — not buried in the identity panel.
//
// Copy uses the modern Clipboard API with a graceful fallback to
// the legacy execCommand path for older browsers / non-HTTPS dev
// environments. A 1.5s "Copied!" pill confirms the action.

import { useState } from 'react';
import { motion } from 'motion/react';
import Icon from '@baaligames/shared-ui/components/Icon';
import { useT } from '@baaligames/shared-ui/lib/i18n';

export default function ShareableIdCard({ uaid }: { uaid: string }) {
  const t = useT();
  const [copied, setCopied] = useState(false);

  async function copy() {
    try {
      if (navigator.clipboard && window.isSecureContext) {
        await navigator.clipboard.writeText(uaid);
      } else {
        const ta = document.createElement('textarea');
        ta.value = uaid;
        ta.style.position = 'fixed'; ta.style.opacity = '0';
        document.body.appendChild(ta);
        ta.select();
        document.execCommand('copy');
        document.body.removeChild(ta);
      }
      setCopied(true);
      setTimeout(() => setCopied(false), 1500);
    } catch {
      // Silent — agent can long-press the visible code to copy
      // manually if the API path failed.
    }
  }

  return (
    <motion.section
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.25 }}
      className="rounded-2xl p-5
                 bg-gradient-to-br from-cyan-500/10 to-indigo-500/10
                 dark:from-cyan-400/[0.08] dark:to-indigo-400/[0.08]
                 border border-cyan-300/40 dark:border-cyan-400/30
                 shadow-[0_1px_3px_rgba(0,0,0,0.04)] dark:shadow-none"
    >
      <div className="flex items-start gap-3">
        <span className="grid place-items-center w-11 h-11 rounded-xl shrink-0
                         bg-cyan-500/20 text-cyan-700 dark:text-cyan-300">
          <Icon name="badge" size={22} filled />
        </span>
        <div className="flex-1 min-w-0">
          <div className="text-[10px] uppercase tracking-[.22em] font-extrabold
                          text-cyan-700 dark:text-cyan-300">
            {t('agent.shareableId.eyebrow')}
          </div>
          <div className="mt-0.5 flex items-baseline gap-2 flex-wrap">
            <span className="font-mono tabular-nums tracking-[.18em] text-2xl font-black
                             text-arena-900 dark:text-white select-all">
              {uaid}
            </span>
            <button
              onClick={copy}
              type="button"
              className={`inline-flex items-center gap-1 px-2.5 py-1 rounded-full text-[10.5px] font-bold uppercase tracking-wider transition
                          ${copied
                            ? 'bg-emerald-600 text-white'
                            : 'bg-white dark:bg-arena-600/40 text-arena-700 dark:text-neon-100/85 border border-arena-200/60 dark:border-arena-600/50 hover:bg-arena-50/40 dark:hover:bg-white/[0.1]'}`}
              aria-label={copied ? t('agent.shareableId.tooltip.copied') : t('agent.shareableId.tooltip.copy')}
            >
              <Icon name={copied ? 'check' : 'content_copy'} size={11} filled={copied} />
              {copied ? t('agent.shareableId.cta.copied') : t('agent.shareableId.cta.copy')}
            </button>
          </div>
          <p className="mt-2 text-[11.5px] text-arena-700 dark:text-neon-200/75 leading-relaxed">
            {t('agent.shareableId.body.before')}<strong>{t('agent.shareableId.body.not')}</strong>{t('agent.shareableId.body.after')}
          </p>
        </div>
      </div>
    </motion.section>
  );
}
